import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { getMaxUploadSizeByChannel } from 'shared/helpers/fileTypeHelper';
import { DirectUpload } from 'activestorage';
import { MAXIMUM_FILE_UPLOAD_SIZE } from 'shared/constants/messages';
import { convertToWav } from 'dashboard/components/widgets/WootWriter/utils/mp3ConversionUtils';

// Instagram only supports: aac, m4a, wav, mp4 for audio
// MP3 (audio/mpeg), OGG, WebM are NOT supported, so we convert them to WAV
const INSTAGRAM_UNSUPPORTED_AUDIO_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/ogg',
  'audio/webm',
];

const isInstagramChannel = inbox => {
  if (!inbox) return false;
  const channelType = inbox.channel_type?.toLowerCase() || '';
  return (
    channelType.includes('instagram') ||
    channelType === 'channel::instagram'
  );
};

const shouldConvertAudioForInstagram = (inbox, mimeType) => {
  if (!isInstagramChannel(inbox)) return false;
  if (!mimeType?.startsWith('audio/')) return false;
  return INSTAGRAM_UNSUPPORTED_AUDIO_TYPES.includes(mimeType);
};

export default {
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
  },
  methods: {
    /**
     * Convert audio file to WAV format for Instagram compatibility
     * Instagram only supports: aac, m4a, wav, mp4
     * Uses the same convertToWav function used by AudioRecorder
     */
    async convertAudioForInstagram(file) {
      try {
        const originalFile = file.file;
        const wavBlob = await convertToWav(originalFile);
        const wavFileName = originalFile.name.replace(/\.[^/.]+$/, '.wav');
        const wavFile = new File([wavBlob], wavFileName, { type: 'audio/wav' });

        return {
          ...file,
          name: wavFileName,
          type: 'audio/wav',
          size: wavFile.size,
          file: wavFile,
        };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to convert audio for Instagram:', error);
        throw new Error(
          this.$t('CONVERSATION.INSTAGRAM_AUDIO_CONVERSION_ERROR') ||
            'Failed to convert audio format for Instagram. Please try a different file.'
        );
      }
    },
    maxSizeFor(mime) {
      // Use default file size limit for private notes
      if (this.isOnPrivateNote) {
        return MAXIMUM_FILE_UPLOAD_SIZE;
      }

      return getMaxUploadSizeByChannel({
        channelType: this.inbox?.channel_type,
        medium: this.inbox?.medium, // e.g. 'sms' | 'whatsapp'
        mime, // e.g. 'image/png'
      });
    },
    alertOverLimit(maxSizeMB) {
      useAlert(
        this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
          MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: maxSizeMB,
        })
      );
    },
    async onFileUpload(file) {
      let fileToUpload = file;

      // Convert unsupported audio formats to WAV for Instagram
      const mimeType = file.file?.type || file.type;
      if (shouldConvertAudioForInstagram(this.inbox, mimeType)) {
        try {
          fileToUpload = await this.convertAudioForInstagram(file);
        } catch (error) {
          useAlert(error.message);
          return Promise.resolve();
        }
      }

      if (this.globalConfig.directUploadsEnabled) {
        return this.onDirectFileUpload(fileToUpload);
      }
      return this.onIndirectFileUpload(fileToUpload);
    },
    async onDirectFileUpload(file) {
      if (!file) return Promise.resolve();

      const mime = file.file?.type || file.type;
      const maxSizeMB = this.maxSizeFor(mime);

      if (!checkFileSizeLimit(file, maxSizeMB)) {
        this.alertOverLimit(maxSizeMB);
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        const upload = new DirectUpload(
          file.file,
          `/api/v1/accounts/${this.accountId}/conversations/${this.currentChat.id}/direct_uploads`,
          {
            directUploadWillCreateBlobWithXHR: xhr => {
              xhr.setRequestHeader(
                'api_access_token',
                this.currentUser.access_token
              );
            },
          }
        );

        upload.create(async (error, blob) => {
          if (error) {
            useAlert(error);
            reject(error);
          } else {
            try {
              await this.attachFile({ file, blob });
              resolve();
            } catch (attachError) {
              reject(attachError);
            }
          }
        });
      });
    },
    async onIndirectFileUpload(file) {
      if (!file) return Promise.resolve();

      const mime = file.file?.type || file.type;
      const maxSizeMB = this.maxSizeFor(mime);

      if (!checkFileSizeLimit(file, maxSizeMB)) {
        this.alertOverLimit(maxSizeMB);
        return Promise.resolve();
      }

      return this.attachFile({ file });
    },
  },
};
